// iter45ap 4b-ii — PaywallSafetyNet
// Extracted lift-out of the Results paywall block so the safety-net
// behaviour is testable in isolation via RTL. Rules:
//   * user.credits > 0 AND project.paid === false
//       → auto-open the ConfirmUnlockModal EXACTLY ONCE (ref-guard)
//       → swap CTA hierarchy: "Use credit" is primary; "Buy report — $149"
//         demotes to secondary
//       → render an unspent-credit banner above the paywall
//   * user.credits === 0 or missing
//       → primary CTA reverts to "Buy report — $149", no banner
//   * project.paid === true
//       → no auto-open, no banner (component may still render, e.g.
//         because Results renders it before the paid-vs-unpaid gate;
//         this is defensive).
//
// The component is display-only: modal ownership stays in Results.jsx.
// We call `onOpenUnlockModal()` at most once via a ref-guard so parent
// state churn (pricing refetch, user re-hydration) does not re-fire.

import React, { useEffect, useRef } from "react";
import { money } from "@/lib/numfmt";
import { getLocale } from "@/lib/i18n";

// a07c — French copy; EN unchanged.
const L = (en, fr) => (getLocale() === "fr" ? fr : en);

export function PaywallSafetyNet({
  user,
  project,
  pricing,
  onOpenUnlockModal,
  onBuyReport,
  onUseCredit,
}) {
  const credits = (user && Number(user.credits)) || 0;
  const projectPaid = Boolean(project && project.paid);
  const shouldAutoOpen = credits > 0 && !projectPaid;

  const openedRef = useRef(false);
  useEffect(() => {
    if (shouldAutoOpen && !openedRef.current) {
      openedRef.current = true;
      onOpenUnlockModal && onOpenUnlockModal();
    }
  }, [shouldAutoOpen, onOpenUnlockModal]);

  const hasCredits = credits > 0 && !projectPaid;
  const tiers = (pricing && pricing.tiers) || [];
  const reportPrice = (pricing && pricing.report) || 99;

  // Bundle-savings nudge. A buyer about to pay for ONE report is the only
  // person on this page who has not seen the per-report maths, so show it
  // here rather than making them go back to /pricing to find it.
  // Anchored on the 5-report bundle: it is the flagged Best Value and the
  // smallest step up that still reads as a decision rather than a commitment.
  const bundle = tiers.find((t) => t.package_id === "credit_bundle_5" && t.kind === "credits");
  const bundlePerReport = bundle
    ? Number(bundle.per_report_usd) || Math.round(bundle.price_usd / bundle.credits)
    : 0;
  const bundleSaving = bundle ? reportPrice - bundlePerReport : 0;
  // Only when they are actually about to buy a single, and only when the
  // bundle genuinely beats it. If a price change ever inverts that, the nudge
  // disappears instead of advertising a saving that is not there.
  const showBundleNudge = !hasCredits && Boolean(bundle) && bundleSaving > 0;

  return (
    <div data-testid="paywall-safety-net">
      {hasCredits && (
        <div
          data-testid="unspent-credit-banner"
          className="mb-4 p-4 rounded-lg"
          style={{
            background: "#E4F3E7",
            border: "1px solid #B9DFC1",
            color: "#1A6F2B",
          }}
        >
          {L("You have ", "Vous avez ")}<strong>{L(`${credits} report credit${credits === 1 ? "" : "s"}`, `${credits} crédit${credits === 1 ? "" : "s"} de rapport`)}</strong>{" "}
          {L("waiting in your wallet — one click unlocks this report.", "en attente dans votre portefeuille — un clic débloque ce rapport.")}
        </div>
      )}

      {showBundleNudge && (
        <div
          data-testid="paywall-bundle-nudge"
          className="mb-4 p-4 rounded-lg flex flex-wrap items-center justify-between gap-3"
          style={{
            background: "var(--paper)",
            border: "1px solid var(--hairline)",
            color: "var(--body)",
          }}
        >
          <div className="text-sm">
            {L(`Buying ${bundle.credits} at once works out at`, `Acheter ${bundle.credits} rapports d'un coup revient à`)}{" "}
            <strong style={{ color: "var(--brand-green)" }}>
              {L(`$${bundlePerReport} per report`, `${money(bundlePerReport)} par rapport`)}
            </strong>{" "}
            {L(`— $${bundleSaving} less than paying $${reportPrice} each. Credits
            unlock any future project.`, `— ${money(bundleSaving)} de moins qu'à ${money(reportPrice)} l'unité. Les crédits débloquent n'importe quel projet futur.`)}
          </div>
          <button
            data-testid="paywall-bundle-nudge-cta"
            onClick={() => onBuyReport && onBuyReport(bundle.package_id)}
            className="btn-secondary whitespace-nowrap"
          >
            {L(`${bundle.credits} reports`, `${bundle.credits} rapports`)} — {money(bundle.price_usd)}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {hasCredits ? (
          <button
            data-testid="paywall-primary-cta"
            onClick={onUseCredit}
            className="btn-primary"
          >
            {L(`Use credit to unlock (${credits} left)`, `Utiliser un crédit pour débloquer (${credits} restant${credits === 1 ? "" : "s"})`)}
          </button>
        ) : (
          <button
            data-testid="paywall-primary-cta"
            onClick={() => onBuyReport && onBuyReport("single_report")}
            className="btn-primary"
          >
            {L(`Buy report — $${reportPrice}`, `Acheter le rapport — ${money(reportPrice)}`)}
          </button>
        )}

        {hasCredits && (
          <button
            data-testid="paywall-secondary-buy-single"
            onClick={() => onBuyReport && onBuyReport("single_report")}
            className="btn-secondary"
          >
            {L(`Buy report — $${reportPrice}`, `Acheter le rapport — ${money(reportPrice)}`)}
          </button>
        )}

        {tiers
          // `single_report` carries kind:"credits" and credits:1, so an
          // unfiltered pass rendered it as "Bundle 1 · $99" directly beside the
          // primary "Buy report — $99" — the same purchase, twice, in one row.
          // Pre-existing; the sixth SKU pushed the row to two lines and made it
          // obvious. A bundle row should contain bundles.
          .filter((t) => t.kind === "credits" && t.package_id !== "single_report")
          .map((tier) => (
            <button
              key={tier.package_id}
              data-testid={`paywall-secondary-${tier.package_id}`}
              onClick={() => onBuyReport && onBuyReport(tier.package_id)}
              className="btn-secondary"
            >
              {L("Bundle", "Pack")} {tier.credits} · {money(tier.price_usd)}
            </button>
          ))}
      </div>
    </div>
  );
}
